import React, { useState, useEffect } from 'react';
import { Minus, Square, X, Copy } from 'lucide-react';
import { isTauri } from '../utils/tauri';

let tauriWindow: any = null;

// 창 버튼은 Windows 표준 캡션 버튼과 같은 46×32px. 색은 tailwind `titlebar` 토큰(제목 표시줄 전용)
const WINDOW_BUTTON =
  'h-full w-[46px] inline-flex items-center justify-center text-titlebar-icon transition-colors';

export const TitleBar: React.FC = () => {
  const [isMaximized, setIsMaximized] = useState(false);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    if (!isTauri()) return;

    import('@tauri-apps/api/window').then((mod) => {
      tauriWindow = mod.getCurrentWindow();
      tauriWindow.isMaximized().then((m: boolean) => setIsMaximized(m));
      setReady(true);
    });

    const handleResize = async () => {
      if (tauriWindow) {
        setIsMaximized(await tauriWindow.isMaximized());
      }
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // 창 전체를 덮는 오버레이(관리자 콘솔·도움말·서랍·통화 화면·우상단 배너)가 이 막대 아래에서 시작하도록
  // 높이를 CSS 변수로 알린다(`top-[var(--titlebar-h,0px)]`). 덮이면 좁은 창에서 창을 옮기거나 닫을 수 없다.
  // ⚠ 값은 아래 막대의 `h-8`(2rem)과 같아야 한다. 막대가 없을 때(웹·준비 전)는 변수가 없어 0px로 접힌다.
  useEffect(() => {
    if (!ready) return;
    const rootStyle = document.documentElement.style;
    rootStyle.setProperty('--titlebar-h', '2rem');
    return () => {
      rootStyle.removeProperty('--titlebar-h');
    };
  }, [ready]);

  if (!isTauri() || !ready) return null;

  // ─ 는 작업표시줄로 최소화, × 만 트레이로 숨긴다(CloseRequested → hide).
  // Rust `minimize_window`가 tao의 표시 플래그를 실제 상태에 맞춘 뒤 최소화한다 — JS의
  // minimize()를 바로 부르면 알림 점멸로 떠오른 창에서 최소화 대신 트레이로 숨는다.
  const handleMinimize = async () => {
    try {
      const { invoke } = await import('@tauri-apps/api/core');
      await invoke('minimize_window');
    } catch {
      if (tauriWindow) await tauriWindow.minimize();
    }
  };

  const handleMaximize = async () => {
    if (tauriWindow) {
      await tauriWindow.toggleMaximize();
      setIsMaximized(await tauriWindow.isMaximized());
    }
  };

  const handleClose = async () => {
    if (tauriWindow) await tauriWindow.close();
  };

  return (
    <div
      className="h-8 bg-titlebar border-b border-titlebar-line flex items-center justify-between select-none shrink-0"
      style={{ WebkitAppRegion: 'drag' } as React.CSSProperties}
    >
      <div className="flex items-center gap-2 pl-3">
        <img src="/icon.png" alt="" className="h-4 w-4 rounded" />
        <span className="text-[13px] leading-4 text-titlebar-fg font-semibold">INA-TALK</span>
      </div>

      <div
        className="flex h-full"
        style={{ WebkitAppRegion: 'no-drag' } as React.CSSProperties}
      >
        <button
          onClick={handleMinimize}
          className={`${WINDOW_BUTTON} hover:bg-titlebar-hover hover:text-titlebar-fg`}
          title="최소화"
        >
          <Minus size={16} strokeWidth={2.5} />
        </button>
        <button
          onClick={handleMaximize}
          className={`${WINDOW_BUTTON} hover:bg-titlebar-hover hover:text-titlebar-fg`}
          title={isMaximized ? '이전 크기로 복원' : '최대화'}
        >
          {isMaximized ? <Copy size={14} strokeWidth={2.5} /> : <Square size={14} strokeWidth={2.5} />}
        </button>
        {/* 닫기 hover는 앱의 레드 소유자인 brand-600(흰 아이콘 4.84:1). 예전엔 raw red-600이었다 */}
        <button
          onClick={handleClose}
          className={`${WINDOW_BUTTON} hover:bg-titlebar-close hover:text-white`}
          title="닫기 (트레이로 이동)"
        >
          <X size={16} strokeWidth={2.5} />
        </button>
      </div>
    </div>
  );
};
